import {Injectable} from '@angular/core';
import {RequestService} from "../request/request.service";
import {CmdbNodeRecordModel} from "./cmdb-node-record.model";
import {CmdbRelationshipRecordModel} from "./cmdb-relationship-record.model";
import {CmdbPeerRelationshipRecordModel} from "./cmdb-peer-relationship-record.model";
import {CmdbRelationshipTypeRecordModel} from "./cmdb-relationship-type-record.model";
import {CmdbPeerRelationshipTypeRecordModel} from "./cmdb-peer-relationship-type-record.model";
import {CmdbMetadataRecordModel} from "./cmdb-metadata-record.model";
import {LegacyInitializer} from "../initialization/legacy.initializer";
import {CmdbLogicalClassificationRecordModel} from "./cmdb-logical-classification-record.model";
import {CmdbTableHierarchyModel} from "./cmdb-table-hierarchy.model";
import {ReplaySubject} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class CmdbDataService {

  cmdbContentLoaded: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private beingInited = false;

  private inited = false;

  private relationshipTypes: CmdbRelationshipTypeRecordModel[] = [];
  private peerRelationshipTypes: CmdbPeerRelationshipTypeRecordModel[] = [];

  private metadata: CmdbMetadataRecordModel[] = [];
  private logicalClassifications: CmdbLogicalClassificationRecordModel[] = [];
  private tableHierarchy: CmdbTableHierarchyModel = {};

  private cmdbCharacterFontSizes = {};

  constructor(private requestService: RequestService, private legacyInitializer: LegacyInitializer) {

    this.legacyInitializer.legacyLoaded.subscribe((e) => {
      (window as any).WebsocketAPI.registerAPIImplementation("cmdbContent", (data: any, responseBody: any) => {
        this.relationshipTypes = [];
        this.peerRelationshipTypes = [];
        this.metadata = [];
        this.logicalClassifications = [];
        this.tableHierarchy = {};
        this.init().then(() => {
          this.cmdbContentLoaded.next(false);
        });
      });

      this.init().then(() => {
        this.cmdbContentLoaded.next(true);
      });
    });

  }

  async getNodes(): Promise<CmdbNodeRecordModel[]> {

    const options = {
      page: 0,
      page_size: 100,
      include_empty_fields: true,
      fields: "*",
      displayValues: "*",
    };

    return await this.getRestData(options, "CMDB");
  }

  async getNode(id: string): Promise<CmdbNodeRecordModel[]> {
    const options = {
      page: 0,
      page_size: 100,
      include_empty_fields: true,
      fields: "*",
      displayValues: "*",
    };

    const node = await this.getRestData(options, "CMDB", id);
    return [node];
  }

  async getRelationships(): Promise<CmdbRelationshipRecordModel[]> {
    const options = {
      page: 0,
      page_size: 100,
      include_empty_fields: true,
      fields: "*",
      displayValues: "*",
    };

    return await this.getRestData(options, "CMDBRelationship");
  }

  async getRelationshipsFor(id: string): Promise<CmdbRelationshipRecordModel[]> {
    const pOptions = {
      Provider: id,
      page: 0,
      page_size: 100,
      include_empty_fields: true,
      fields: "*",
      displayValues: "*",
    };

    const providers = await this.getRestData(pOptions, "CMDBRelationship");

    const dOptions = {
      Dependent: id,
      page: 0,
      page_size: 100,
      include_empty_fields: true,
      fields: "*",
      displayValues: "*",
    };

    const dependents = await this.getRestData(dOptions, "CMDBRelationship");

    return [...providers, ...dependents];
  }

  async getPeerRelationships(): Promise<CmdbPeerRelationshipRecordModel[]> {
    const options = {
      page: 0,
      page_size: 100,
      include_empty_fields: true,
      fields: "*",
      displayValues: "*",
      relations: "Peers",
      inlineRelations: false,
    };
    const peerRelationships =  await this.getRestData(options, "CMDBPeerRelationship");

    peerRelationships.forEach(rel => {
      rel.peers = rel.Peers || [];
    });

    return peerRelationships;
  }

  async getPeerRelationshipsFor(id: string): Promise<CmdbPeerRelationshipRecordModel[]> {
    const options = {
      query: {operator: "and", value: [{operator: "in", relation: "Peers", value: id}] },
      page: 0,
      page_size: 100,
      include_empty_fields: true,
      fields: "*",
      displayValues: "*",
      relations: "Peers",
      inlineRelations: false,
    };
    const peerRelationships =  await this.getRestData(options, "CMDBPeerRelationship");

    peerRelationships.forEach(rel => {
      rel.peers = rel.Peers || [];
    });

    return peerRelationships;
  }

  getRelationshipTypes(): CmdbRelationshipTypeRecordModel[] {
    return this.relationshipTypes;
  }

  getPeerRelationshipTypes(): CmdbPeerRelationshipTypeRecordModel[] {
    return this.peerRelationshipTypes;
  }

  getMetadata(): CmdbMetadataRecordModel[] {
    return this.metadata;
  }

  getLogicalClassifications(): CmdbLogicalClassificationRecordModel[] {
    return this.logicalClassifications;
  }

  getTableHierarchy(): CmdbTableHierarchyModel {
    return this.tableHierarchy;
  }

  getCmdbCharacterFontSizes(): any {
    if (Object.keys(this.cmdbCharacterFontSizes).length === 0) {
      // The base theme stylesheet always has the CMDB rules in it.
      const stylesheet = Array.from(document.styleSheets).find((s) => (s.ownerNode as any).id == "sb-base-theme");
      // Find all the rules that are CSSStyleRule and match an CMDB ::before rule, e.g.
      //
      // .icon-cmdb-e658:before,
      // .icon-cmdb-vpn:before {
      //     content: '\e658';
      //     font-size: 24pt;
      //   }
      //
      const cmdbRules = Array.from(stylesheet.cssRules)
        .filter((x) => x instanceof CSSStyleRule && x.selectorText.startsWith(".icon-cmdb-e") && x.selectorText.endsWith("::before"))
        .map((x) => x as CSSStyleRule);
      // Map the font-size to the shortened code, e.g. 'e658' which is '.icon-cmdb-e658:before'.substr(11).slice(0, -8)
      this.cmdbCharacterFontSizes = cmdbRules.reduce((acc, next) => {
        next.selectorText
          .replace(/ /g, "")
          .split(',')
          .forEach((name) => {
            const fontSize = next.style.fontSize;
            acc[name.substr(11).slice(0, -8)] = fontSize;
          });
        return acc;
      }, {});
    }
    return this.cmdbCharacterFontSizes;
  }

  private async init() {
    if (this.beingInited) {
      return;
    }

    if (Object.keys(this.tableHierarchy).length === 0) {

      this.beingInited = true;

      const options = {
        page: 0,
        page_size: 10000,
        include_empty_fields: true,
        fields: "*",
        displayValues: "*",
      };

      this.relationshipTypes = await this.getRestData(options, "CMDBRelationshipType");
      this.peerRelationshipTypes = await this.getRestData(options,"CMDBPeerRelationshipType");
      this.metadata = await this.getRestData(options,"CMDBMetadata");
      this.logicalClassifications = await this.getRestData(options,"CMDBLogicalClassification");
      this.tableHierarchy = await this.getControllerData("getTableHierarchy");

      this.beingInited = false;
      this.inited = true;

      this.cmdbContentLoaded.next(true);
    }
  }

  private async getControllerData(method: string) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.CMDB")
      .method(method)
      .execute();

    return response.data;
  }

  private async getRestData(options: any, table: string, id?: string) {
    const response = await (window as any).REST.get(table, (id ? id : null), options);
    return response.data;
  }
}
